import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Button } from '@mui/material';

export default function LapComparison({ fastestLap, otherLap, corners, backendUrl }) {
  const [analysis, setAnalysis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCompare = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = Cookies.get('session_token');
      const res = await axios.post(
        `${backendUrl}/compare_laps`,
        {
          fastest_lap: fastestLap,
          other_lap: otherLap,
          corners: corners,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      setAnalysis(res.data.analysis);
    } catch (err) {
      setError('Failed to fetch analysis.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#222', color: '#fff', padding: 24, borderRadius: 8 }}>
      <h2>Lap Comparison Analysis</h2>
      <Button
        variant="contained"
        color="primary"
        onClick={handleCompare}
        disabled={loading}
        size="small"
        sx={{
          mb: 2,
          fontWeight: 700,
          fontSize: '0.95rem',
          borderRadius: 1,
          boxShadow: 'none',
          textTransform: 'none',
          px: 2.2,
          py: 0.7,
          background: '#0072CE',
          '&:hover': {
            background: '#005fa3',
          },
        }}
      >
        {loading ? 'Analyzing...' : 'Compare Laps'}
      </Button>
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {analysis.length > 0 && (
        <table style={{ width: '100%', background: '#333', borderRadius: 4, borderCollapse: 'collapse', border: '2px solid rgba(200,200,200,0.5)' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid rgba(200,200,200,0.5)', padding: '8px' }}>Corner</th>
              <th style={{ border: '1px solid rgba(200,200,200,0.5)', padding: '8px' }}>Time Loss (s)</th>
              <th style={{ border: '1px solid rgba(200,200,200,0.5)', padding: '8px' }}>Main Cause</th>
              <th style={{ border: '1px solid rgba(200,200,200,0.5)', padding: '8px' }}>Suggestion</th>
            </tr>
          </thead>
          <tbody>
            {analysis.map((row, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid rgba(200,200,200,0.5)', padding: '8px' }}>{row.corner_name}</td>
                <td style={{ border: '1px solid rgba(200,200,200,0.5)', padding: '8px' }}>{row.time_delta > 0 ? `+${row.time_delta}` : row.time_delta}</td>
                <td style={{ border: '1px solid rgba(200,200,200,0.5)', padding: '8px' }}>
                  {row.causes && Array.isArray(row.causes) ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {row.causes.map((cause, idx) => (
                        <li key={idx}>
                          Phase: <b>{cause.phase}</b> #{cause.index + 1}, Fast: {cause.avg_fast}, Other: {cause.avg_other}, Diff: {cause.difference}, Result: <b>{cause.result}</b>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: (() => {
                          const text = row.main_cause || row.cause || '';
                          // Split on first \n
                          const firstNewline = text.indexOf('\n');
                          let firstLine, rest;
                          if (firstNewline === -1) {
                            firstLine = text;
                            rest = '';
                          } else {
                            firstLine = text.slice(0, firstNewline);
                            rest = text.slice(firstNewline);
                          }
                          // Format first line bold, then process the rest
                          let html = `<b>${firstLine}</b>${rest}`;
                          // Now handle tabs and newlines
                          html = html
                            .replace(/(?:\n)?(\t+)/g, (match, tabs) => {
                              const br = match.startsWith('\n') ? '<br/>' : '';
                              return br + '&nbsp;'.repeat(4 * tabs.length);
                            })
                            .replace(/\n/g, '<br/>');
                          return html;
                        })()
                      }}
                    />
                  )}
                </td>
                <td style={{ border: '1px solid rgba(200,200,200,0.5)', padding: '8px' }}>{row.suggestion}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

LapComparison.propTypes = {
  fastestLap: PropTypes.array.isRequired,
  otherLap: PropTypes.array.isRequired,
  corners: PropTypes.array.isRequired,
  backendUrl: PropTypes.string.isRequired,
};
